import { useNavigate, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RefreshCw, Home, WifiOff, CloudHail } from "lucide-react";

function isDynamicImportError(error: Error | undefined): boolean {
  const message = error?.message?.toLowerCase() || "";
  return (
    message.includes("dynamically imported module") ||
    message.includes("failed to fetch") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    error?.name === "ChunkLoadError"
  );
}

export function Error({ error }: ErrorComponentProps) {
  const navigate = useNavigate();
  // Routes can throw any value. This component shadows the global `Error`, so
  // narrow through `globalThis`.
  const routeError = error instanceof globalThis.Error ? error : undefined;
  const isDev = process.env.NODE_ENV === "development";
  const isOffline = !navigator.onLine;
  const isChunkError = isDynamicImportError(routeError);

  // Log error in production but don't show to user
  if (!isDev) {
    console.error(error);
  }

  // Handle offline/chunk loading errors
  if (isOffline || isChunkError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="max-w-lg w-full p-8 space-y-6 text-center">
          <div className="space-y-4">
            <WifiOff className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="text-2xl font-bold text-gray-900">
              {isOffline ? "You're offline" : "Connection issue"}
            </h1>
            <p className="text-gray-600">
              {isOffline
                ? "Please check your internet connection and try again."
                : "We had trouble loading this page. This can happen with a slow or unstable connection."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
      <Card className="max-w-lg w-full p-8 space-y-6 text-center">
        <div className="space-y-4">
          <CloudHail className="w-12 h-12 mx-auto text-red-500" />
          <h1 className="text-2xl font-bold text-gray-900">
            Oops! Something went wrong
          </h1>
          <p className="text-gray-600">
            {isDev
              ? routeError?.message ||
                "An unexpected error occurred. Please try again."
              : "We ran into an issue. Please try refreshing or return home."}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            onClick={() => navigate({ to: "/" })}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Go to Home
          </Button>
        </div>
      </Card>
    </div>
  );
}
